import { api, X_AT_KEY } from '@alltrails/core/utils/api';
import { destroy } from '@alltrails/core/utils/destroy';
import { get } from '@alltrails/core/utils/get';
import { post } from '@alltrails/core/utils/post';
import { put } from '@alltrails/core/utils/put';
import type List from '@alltrails/types/List';
import type { Visibility } from '@alltrails/types/Visibility';
import type ListItem from '../types/ListItem';

type Meta = {
  items: number;
  status: 'ok';
  timestamp: string;
};

type Errors = {
  code: string;
  message: string;
};

type ListsResponse = {
  lists: List[];
  meta: Meta;
};

type ListsUpdateResponse = {
  lists?: List[];
  meta: Meta;
  errors?: Errors[];
};

export const createList = (name: string, privacy: Visibility) =>
  post<ListsResponse>('/api/alltrails/v2/lists', {
    name,
    contentPrivacy: privacy
  });

export const updateList = ({ id, name, description, contentPrivacy }: List) =>
  put<ListsUpdateResponse>(`/api/alltrails/lists/${id}`, {
    name,
    description,
    contentPrivacy
  });

type ListItemsResponse = {
  listItems?: ListItem[];
  meta: Meta;
  errors?: Errors[];
};

export const addToList = (listId: number, type: string, objectId: number, userId: number) => {
  const data: { object_id: number; type: string; include_obj: boolean; user_id?: number } = {
    object_id: objectId,
    type,
    include_obj: true
  };

  // Defaults request to updating own list unless a user id is specified
  if (userId) {
    data.user_id = userId;
  }

  return post<ListItemsResponse>(`/api/alltrails/v2/lists/${listId}/items`, data);
};

export const removeFromList = (listId: number, listItemId: number, userId: number) => {
  const data: { user_id?: number } = {};

  // Defaults request to updating own list unless a user id is specified
  if (userId) {
    data.user_id = userId;
  }

  return api<ListItemsResponse>(`/api/alltrails/v2/lists/${listId}/items/${listItemId}`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify(data)
  });
};

type DeleteListsResponse = {
  lists: List[];
};

export const deleteList = (id: number) => destroy<DeleteListsResponse>(`/api/alltrails/lists/${id}`);

type FetchListsResponse = ListsResponse & {
  pageInfo: {
    hasNextPage: boolean;
    itemCount: number;
    nextCursor?: string;
  };
};

export const fetchLists = (profileOwnerId: number, baseUrl?: string): Promise<FetchListsResponse> =>
  get<FetchListsResponse>(`${baseUrl ?? ''}/api/alltrails/users/${profileOwnerId}/lists`, {
    params: {
      api_key: X_AT_KEY
    }
  });

export const fetchListsNextPage = (
  profileOwnerId: number,
  nextCursor: string,
  baseUrl?: string
): Promise<FetchListsResponse> =>
  get<FetchListsResponse>(`${baseUrl ?? ''}/api/alltrails/users/${profileOwnerId}/lists`, {
    params: {
      api_key: X_AT_KEY,
      after: nextCursor
    }
  });

type CopyToMyListsResponse = {
  lists: List[];
};

export const copyToMyLists = (listId: number) =>
  post<CopyToMyListsResponse>(`/api/alltrails/lists/${listId}/copy`, {});

type ListCollaborator = {
  user: unknown;
  links: { rel: string; href: string; method: RequestInit['method'] }[];
  metadata?: { label?: string };
};

type PageInfo = {
  hasNextPage: boolean;
  itemCount: number;
  totalItemCount: number;
};

type GetCollaboratorsResponse = {
  collaborators: ListCollaborator[];
  pageInfo: PageInfo;
};

export const fetchCollaborators = (listId: number): Promise<GetCollaboratorsResponse> =>
  get<GetCollaboratorsResponse>(`/api/alltrails/lists/${listId}/collaborators`);

type InvitationLinkResponse = {
  url: string;
};

export const generateInvitationLink = (listId: number): Promise<InvitationLinkResponse> =>
  post<InvitationLinkResponse>(`/api/alltrails/lists/${listId}/invitations`, {});

type DeleteCollaboratorResponse = {
  collaborators: ListCollaborator[];
};

export const deleteCollaborator = (url: string) =>
  destroy<DeleteCollaboratorResponse>(url);

export const transferOwnership = (listId: number, userId: number) =>
  put<ListsResponse>(`/api/alltrails/lists/${listId}/owner/${userId}`, {});

type ListAlertsResponse = {
  title: string;
  description: string;
  type: string;
};

export const fetchListAlerts = (listId: number) =>
  get<ListAlertsResponse>(`/api/alltrails/lists/${listId}/alert`);

type ListSyncResponse = {
  deleted_ids: string[];
  meta: Meta;
  new_ids: string[];
  updated_ids: string[];
};

export const listSync = (lastSyncTime: string): Promise<ListSyncResponse> =>
  get<ListSyncResponse>(`/api/alltrails/lists/sync`, {
    params: {
      last_sync_time: lastSyncTime
    }
  });

export type ListItemsV2 = {
  listItems: ListItem[];
  meta: Meta;
};

export const getListItems = (listId: number): Promise<ListItemsV2> =>
  get<ListItemsV2>(`/api/alltrails/v2/lists/${listId}/items`, {
    params: {
      include_obj: true
    }
  });

export type ListDetails = {
  lists: List[];
  meta: Meta;
};

export const getListDetails = (listId: number): Promise<ListDetails> =>
  get<ListDetails>(`/api/alltrails/lists/${listId}`);
